import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text-main)" }}>
      <header className="border-b" style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="VeriHandi" className="h-8 w-8" />
            <span className="font-display font-black tracking-tighter text-xl">VeriHandi</span>
          </Link>
          <span className="label-caps ml-auto">Privacy Notice</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6 leading-relaxed">
        <Link to="/" className="inline-flex items-center gap-2 text-sm hover:underline" style={{ color: "var(--text-muted)" }}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tighter">Privacy Notice</h1>
        <p style={{ color: "var(--text-muted)" }}>Last updated: February 2026 · Data controller: VeriHandi · contact: hello@verihandi.com</p>

        <section className="card-tactile rounded-2xl p-6 space-y-3">
          <h2 className="font-display font-bold text-xl">What we store</h2>
          <ul className="list-disc pl-5 text-sm space-y-1.5">
            <li><b>Device identifier</b> — a random UUID generated in your browser (localStorage) so your data on this device stays linked to you. No account, no email required.</li>
            <li><b>Your content</b> — prescriptions, doses, todos, shopping items, notes, reactions, caregivers — stored in our EU-region database, scoped to your device identifier.</li>
            <li><b>Push subscription endpoint</b> — if you enable reminders, your browser's push endpoint (Apple/Google) is stored so we can deliver notifications at dose time.</li>
            <li><b>Preferences</b> — theme, favourites, sidebar state — in localStorage only (never sent to our server).</li>
          </ul>
        </section>

        <section className="card-tactile rounded-2xl p-6 space-y-3">
          <h2 className="font-display font-bold text-xl">Cookies & analytics</h2>
          <p className="text-sm">We use <b>optional</b>, anonymized analytics (Google Analytics 4 / Google Tag Manager) to understand which tools people use. Loaded only after you tap <b>Accept</b> on the consent banner. IP addresses are anonymized. You can withdraw consent at any time in Settings.</p>
          <p className="text-sm">Essential storage (localStorage for your device identifier, preferences, login state for prescriptions) does not require consent — it is strictly necessary.</p>
        </section>

        <section className="card-tactile rounded-2xl p-6 space-y-3">
          <h2 className="font-display font-bold text-xl">Third parties</h2>
          <ul className="list-disc pl-5 text-sm space-y-1.5">
            <li><b>Stripe</b> — if you donate, Stripe (UK/Ireland) processes the payment. We never see card details.</li>
            <li><b>Google Calendar</b> — only if you explicitly connect your Google account to push a schedule. You can disconnect at any time.</li>
            <li><b>Gemini (Google) via Emergent</b> — when you upload a prescription label, the image is sent for AI analysis and not retained by us.</li>
            <li><b>Web Push (Apple / Google)</b> — delivers our reminder notifications to your device.</li>
          </ul>
        </section>

        <section className="card-tactile rounded-2xl p-6 space-y-3">
          <h2 className="font-display font-bold text-xl">Your rights (GDPR / UK GDPR)</h2>
          <ul className="list-disc pl-5 text-sm space-y-1.5">
            <li>Access, rectification, erasure, restriction, portability, objection.</li>
            <li>Withdraw analytics consent any time in Settings.</li>
            <li>Delete all data on this device: Settings → <i>Forget this device</i>, or clear your browser storage for <code>verihandi.com</code>.</li>
            <li>Complain to your supervisory authority (UK: ICO; EU: your national DPA).</li>
          </ul>
        </section>

        <section className="card-tactile rounded-2xl p-6 space-y-3">
          <h2 className="font-display font-bold text-xl">Retention</h2>
          <p className="text-sm">Your prescriptions and content are retained until you delete them. Push subscriptions are removed automatically when they expire. Donation records (required for tax/accounting) are retained for 7 years.</p>
        </section>

        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          This notice can evolve. We'll prompt you again if material changes affect analytics or cookies.
        </p>
      </main>
    </div>
  );
}
